import { useState } from 'react'
import { Cross2Icon, PlusIcon } from '@radix-ui/react-icons'
import { TextInput } from '@/components/InputFields'

type Medication = { name: string }

type AddMedicationProps = {
  medications: Medication[]
  onChange: (medications: Medication[]) => void
  placeholder?: string
}

const AddMedication = ({
  medications,
  onChange,
  placeholder = 'Enter medication here',
}: AddMedicationProps) => {
  const [inputValue, setInputValue] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const addMedication = () => {
    const trimmed = inputValue.trim()
    if (trimmed === '') return
    if (medications.some((medication) => medication.name === trimmed)) {
      setErrorMessage('This medication has already been added')
      return
    }
    setErrorMessage(null)
    onChange([...medications, { name: trimmed }])
    setInputValue('')
  }

  const removeMedication = (index: number) => {
    onChange(medications.filter((_, i) => i !== index))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      addMedication()
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <TextInput
          placeholder={placeholder}
          value={inputValue}
          onChange={(event) => {
            setInputValue(event.target.value)
            setErrorMessage(null)
          }}
          onKeyDown={handleKeyDown}
          aria-invalid={!!errorMessage}
          className="flex-1"
        />
        <button
          type="button"
          onClick={addMedication}
          disabled={inputValue.trim() === ''}
          aria-label="Add medication"
          className="flex h-10 items-center justify-center rounded-md bg-primary px-2 text-primary-contrast disabled:opacity-50"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>
      {errorMessage && (
        <p className="text-sm text-error" role="alert">
          {errorMessage}
        </p>
      )}
      {medications.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {medications.map((medication, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-2 rounded-md bg-secondary px-2 py-1 text-sm font-semibold text-primary-contrast"
            >
              {medication.name.toLowerCase()}
              <button
                type="button"
                onClick={() => removeMedication(index)}
                aria-label={`Remove ${medication.name}`}
                className="rounded-full p-0.5 hover:bg-primary-contrast/10"
              >
                <Cross2Icon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export default AddMedication
